<template>
  <div class="flex w-full items-center justify-center desktop:px-[100px]">
    <highcharts
      :options="chartOptions"
      :ref="chartRef"
    />
  </div>
</template>

<script setup lang="ts">
import { Point, type TooltipFormatterContextObject } from 'highcharts'
import { computed } from 'vue'
import { useBrowserResize } from 'xangle-ui/src/composables/useBrowserResize'

import type { LivewatchTotalSupplyBreakdownInfo } from '~/api/service/livewatch/total-supply-breakdown.service'
import { setCommaWithDigits } from '~/utils/numberFormat'

interface Props {
  data: LivewatchTotalSupplyBreakdownInfo
}

const prop = defineProps<Props>()
const chartRef = ref<any>(null)
const { createCustomTooltip } = useChartTooltip()
const { isMobile } = useBrowserResize()
// 임시 다국어 함수
const t = (key: string) => key

// 임시 워터마크 함수
function addWatermark(chart: any, type: string, watermark: string) {
  // 워터마크 기능이 필요하다면 여기에 구현
  return null
}

// 임시 워터마크 텍스트
const waterMark = 'WATERMARK'

// Sankey 차트용 임시 키 생성 함수
function generateSankeyKey(model: any) {
  return [ 'from', 'to', 'weight', 'color' ]
}

// Sankey 차트용 임시 데이터 생성 함수
function generateSankeyData(model: any) {
  return [
    {
      from: 'Total Issued',
      to: 'Burned',
      weight: prop.data.totalBurned,
      color: '#AD3DF9'
    },
    {
      from: 'Total Issued',
      to: 'Total Supply',
      weight: prop.data.totalSupply,
      color: '#5b8cff'
    },
    {
      from: 'Total Supply',
      to: 'Non-Circulating Supply',
      weight: prop.data.nonCirculatingSupply,
      color: '#ff6f91'
    },
    {
      from: 'Total Supply',
      to: 'Circulating Supply',
      weight: prop.data.circulatingSupply,
      color: '#ffe156'
    },
  ]
}

const chartOptions = computed(() => {
    
  return {
    plotOptions: {
      series: {
        marker: { radius: 0 }
      }
    },
    title: null,
    scrollbar: { enabled: false },
    chart: {
      width: isMobile.value ? 300 : 1024,
      height: isMobile.value ? 200 : 300,
      backgroundColor: 'transparent',
      events: {
        load: function (this: any) {
          return addWatermark(this, 'sankey', waterMark)
        },
      },
      zooming: { mouseWheel: { enabled: false } }
    },
    series: [ {
      linkOpacity: 0.2,
      states: { hover: { linkOpacity: 0.4 } },
      colorByPoint: true,
      centerInCategory: true,
      borderWidth: 30,
      borderColor: 'transparent',
      minLinkWidth: 1,
      nodePadding: 60,
      curveFactor: 0.4,
      stickyTracking: true,
      relativeXValue: true,
      dataLabels: {
        enabled: !isMobile.value,
        useHTML: true,
        align: 'right',
        verticalAlign: 'middle',
        style: {
          fontFamily: 'Montserrat',
          textOutline: 'none',
          fontWeight: '500',
          fontSize: '12px',
          color: '#fff',
        },
        nodeFormatter: function (this: any) {
          // 예시: "Total Supply"라는 문구만 색상 강조
          if (this.key === 'Total Issued') {
            return `<span class="ml-8">${this.key}</span>`
          }
          // 나머지는 기본 스타일
          return `<span class="mr-8">${this.key}</span>` 
        },
      },
      type: 'sankey',
      keys: generateSankeyKey(prop.data),
      data: generateSankeyData(prop.data),
      nodeWidth: 20,
      borderRadius: 0,
      nodes: [
        {
          id: 'Total Issued',
          name: 'Total Issued',
          color: '#5b8cff',
        },
        {
          id: 'Burned',
          name: 'Burned',
          color: '#AD3DF9',
        },
        {
          id: 'Total Supply',
          name: 'Total Supply',
          color: '#2de2c2',
        },
        {
          id: 'Circulating Supply',
          name: 'Circulating Supply',
          color: '#ffe156',
        },
        {
          id: 'Non-Circulating Supply',
          name: 'Non-Circulating Supply',
          color: '#ff6f91',
        },
      ],
    } ],
    tooltip: {
      split: false,
      shared: true,
      shadow: false,
      outside: true,
      useHTML: true,
      padding: 8,
      backgroundColor: 'none',
      borderWidth: 0,
      borderRadius: 0,
      style: {
        fontSize: '12px',
        fontFamily: 'Montserrat',
      },
      formatter: function (this: TooltipFormatterContextObject) {
        return createCustomTooltip(this, {
          containerClass: 'w-[430px] max-w-[90dvw]',
          content: (point: any) => {
            const type = point.formatPrefix as 'node' | 'point' | 'null'
            if (type === 'point' || type === 'null') {
              // 링크(라인) 툴팁: fromNode, toNode, 두 값의 비교
              const toNode = point.toNode
              const fromNode = point.fromNode
              return `<div class="flex flex-col w-full justify-between gap-2 overflow-hidden flex-wrap">
                    <div class="flex justify-between items-center">
                      <p><span class="mr-2" style="color:${fromNode.color};">●</span>${fromNode.name}</p>
                      <div class="font-bold">${setCommaWithDigits(fromNode.sum, 2)} NXPC</div>
                    </div>
                    <div class="flex justify-between items-center">
                      <p><span class="mr-2" style="color:${toNode.color};">●</span>${toNode.name}</p>
                      <div class="font-bold">${setCommaWithDigits(toNode.sum, 2)} NXPC</div>
                    </div>
                    <div class="flex justify-between items-start">
                      <p class="whitespace-break-spaces">${toNode.name} <br class="desktop:hidden"/>Compared to ${fromNode.name}</p>
                      <div class="font-bold">${(toNode.sum / fromNode.sum * 100).toFixed(2)}%</div>
                    </div>
                  </div>`
            } else {
              // 노드 툴팁: 최상위(root) 노드와 현재 노드의 값
              const seriesNodes = point.series?.nodes || []
              const rootNode = seriesNodes.find((n: any) => n.linksTo?.length === 0) || point
              const currentNode = point

              return `<div class="flex flex-col w-full justify-between gap-2">
                    <div class="flex justify-between items-center">
                      <p><span class="mr-2" style="color:${currentNode.color};">●</span>${currentNode.name}</p>
                      <div class="font-bold">${setCommaWithDigits(currentNode.sum, 2)} NXPC</div>
                    </div>
                    ${currentNode.id !== rootNode.id ? `
                    <div class="flex justify-between items-start">
                      <p class="whitespace-break-spaces">${currentNode.name} <br class="desktop:hidden"/>Compared to ${rootNode.name}</p>
                      <div class="font-bold">${(currentNode.sum / rootNode.sum * 100).toFixed(2)}%</div>
                    </div>` : ''}
                  </div>`
            }
          }
        })
      }
    },
  }
})
</script>

<style scoped lang="scss">
/* 스타일은 필요에 따라 추가 */
</style>